/**
 * @fileoverview Session Persistence Service - Secure persistent storage of encryption session
 *
 * This service provides persistence for the decrypted master key across browser sessions.
 * It stores the master key encrypted with a randomly generated session key in IndexedDB,
 * allowing seamless restoration without user interaction until logout or expiration.
 *
 * **Architecture:**
 * - Generates a random AES-256 session key on first unlock
 * - Encrypts the master key material with this session key
 * - Stores both in IndexedDB (encrypted master key + session key)
 * - On page load, automatically restores the session if not expired
 * - Clears everything on logout
 *
 * **Security Model:**
 * - Session key provides a layer of indirection (not storing raw master key)
 * - Both pieces required to recover (defense in depth)
 * - Expiration enforced based on user preference
 * - Cleared on explicit logout
 *
 * @module services/SessionPersistenceService
 */

import { createLogger } from '@xanectech/lib';

const logger = createLogger('SessionPersistenceService');

/**
 * IndexedDB database name for session persistence
 */
const DB_NAME = 'XanecTechEncryptionSession';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';

/**
 * Structure for persisted session data
 */
export interface PersistedSession {
  userId: string;
  /** Encrypted master key material (encrypted with session key) */
  encryptedMasterKey: string;
  /** Initialization vector for master key encryption */
  iv: string;
  /** Session encryption key (raw bytes as base64) */
  sessionKey: string;
  /** When the session was created */
  createdAt: string;
  /** Session duration in minutes (from user preference) */
  sessionDurationMinutes: number;
}

/**
 * Initialize IndexedDB for session persistence
 */
async function initDB(): Promise<IDBDatabase> {

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      logger.error('Failed to open session database');
      reject(new Error('Failed to open session database'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
  });
}

/**
 * Convert ArrayBuffer to Base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary);
}

/**
 * Convert Base64 string to Uint8Array
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Generate a random session encryption key
 */
async function generateSessionKey(): Promise<CryptoKey> {
  return await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true, // extractable for storage
    ['encrypt', 'decrypt'],
  );
}

/**
 * Encrypt the master key material with a session key
 */
async function encryptWithSessionKey(
  masterKeyMaterial: Uint8Array,
  sessionKey: CryptoKey,
): Promise<{ encrypted: string; iv: string }> {
  // Create IV backed by regular ArrayBuffer
  const ivBuffer = new ArrayBuffer(12);
  const iv = new Uint8Array(ivBuffer);
  window.crypto.getRandomValues(iv);

  // Ensure masterKeyMaterial is backed by a regular ArrayBuffer
  const keyBuffer = new ArrayBuffer(masterKeyMaterial.length);
  new Uint8Array(keyBuffer).set(masterKeyMaterial);

  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    sessionKey,
    keyBuffer,
  );

  return {
    encrypted: arrayBufferToBase64(encryptedBuffer),
    iv: arrayBufferToBase64(iv),
  };
}

/**
 * Decrypt master key material with session key
 */
async function decryptWithSessionKey(
  encryptedData: string,
  iv: string,
  sessionKey: CryptoKey,
): Promise<Uint8Array> {
  const encryptedBytes = base64ToUint8Array(encryptedData);
  const ivBytes = base64ToUint8Array(iv);

  // Ensure buffers are backed by regular ArrayBuffer
  const encryptedBuffer = new ArrayBuffer(encryptedBytes.length);
  new Uint8Array(encryptedBuffer).set(encryptedBytes);

  const ivBuffer = new ArrayBuffer(ivBytes.length);
  new Uint8Array(ivBuffer).set(ivBytes);

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(ivBuffer) },
    sessionKey,
    encryptedBuffer,
  );

  return new Uint8Array(decryptedBuffer);
}

/**
 * Persist an encryption session to IndexedDB
 *
 * Called after successful password or WebAuthn unlock to enable
 * seamless session restoration on page refresh or browser reopen.
 *
 * @param userId - User ID
 * @param masterKeyMaterial - Raw master key bytes to persist
 * @param sessionDurationMinutes - How long the session should remain valid
 */
export async function persistSession(
  userId: string,
  masterKeyMaterial: Uint8Array,
  sessionDurationMinutes: number,
): Promise<void> {

  try {
    // Generate a fresh session key
    const sessionKey = await generateSessionKey();

    // Export session key for storage
    const sessionKeyRaw = await window.crypto.subtle.exportKey(
      'raw',
      sessionKey,
    );
    const sessionKeyBase64 = arrayBufferToBase64(sessionKeyRaw);

    // Encrypt master key with session key
    const { encrypted, iv } = await encryptWithSessionKey(
      masterKeyMaterial,
      sessionKey,
    );

    // Build persisted session data
    const sessionData: PersistedSession = {
      userId,
      encryptedMasterKey: encrypted,
      iv,
      sessionKey: sessionKeyBase64,
      createdAt: new Date().toISOString(),
      sessionDurationMinutes,
    };

    // Store in IndexedDB
    const db = await initDB();

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(sessionData);

      request.onsuccess = () => {
        db.close();
        resolve();
      };

      request.onerror = () => {
        db.close();
        logger.error('Failed to persist session');
        reject(new Error('Failed to persist session'));
      };
    });
  } catch (error) {
    logger.error('Failed to persist session', error as Error);
    throw error;
  }
}

/**
 * Restore an encryption session from IndexedDB
 *
 * Called on page load to check for and restore a valid persisted session.
 * Returns null if no session exists, session has expired, or restoration fails.
 *
 * @param userId - User ID to restore session for
 * @returns Master key material if session is valid, null otherwise
 */
export async function restoreSession(
  userId: string,
): Promise<Uint8Array | null> {

  try {
    const db = await initDB();

    const sessionData = await new Promise<PersistedSession | null>(
      (resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(userId);

        request.onsuccess = () => {
          db.close();
          resolve((request.result as PersistedSession) || null);
        };

        request.onerror = () => {
          db.close();
          logger.error('Failed to retrieve persisted session');
          reject(new Error('Failed to retrieve persisted session'));
        };
      },
    );

    if (!sessionData) {
      return null;
    }

    // Check expiration (duration <= 0 means never expire)
    const createdAt = new Date(sessionData.createdAt);
    const now = new Date();
    const durationMinutes = sessionData.sessionDurationMinutes;

    if (durationMinutes > 0) {
      const expiresAt = new Date(
        createdAt.getTime() + durationMinutes * 60 * 1000,
      );

      if (now > expiresAt) {
        // Clean up expired session
        await clearPersistedSession(userId);
        return null;
      }
    }

    // Import session key - ensure buffer is regular ArrayBuffer
    const sessionKeyRaw = base64ToUint8Array(sessionData.sessionKey);
    const sessionKeyBuffer = new ArrayBuffer(sessionKeyRaw.length);
    new Uint8Array(sessionKeyBuffer).set(sessionKeyRaw);

    const sessionKey = await window.crypto.subtle.importKey(
      'raw',
      sessionKeyBuffer,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt'],
    );

    // Decrypt master key
    const masterKeyMaterial = await decryptWithSessionKey(
      sessionData.encryptedMasterKey,
      sessionData.iv,
      sessionKey,
    );


    return masterKeyMaterial;
  } catch (error) {
    logger.error('Failed to restore session', error as Error);
    return null;
  }
}

/**
 * Clear persisted session for a user
 *
 * Called on logout to remove all persisted session data.
 *
 * @param userId - User ID to clear session for
 */
export async function clearPersistedSession(userId: string): Promise<void> {

  try {
    const db = await initDB();

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(userId);

      request.onsuccess = () => {
        db.close();
        resolve();
      };

      request.onerror = () => {
        db.close();
        logger.error('Failed to clear persisted session');
        reject(new Error('Failed to clear persisted session'));
      };
    });
  } catch (error) {
    logger.error('Failed to clear persisted session', error as Error);
    // Don't throw - clearing session on logout should not block logout
  }
}

/**
 * Check if a valid persisted session exists
 *
 * Quick check without full restoration - useful for UI decisions.
 *
 * @param userId - User ID to check
 * @returns True if a valid (non-expired) session exists
 */
export async function hasPersistedSession(userId: string): Promise<boolean> {

  try {
    const db = await initDB();

    const sessionData = await new Promise<PersistedSession | null>(
      (resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(userId);

        request.onsuccess = () => {
          db.close();
          resolve((request.result as PersistedSession) || null);
        };

        request.onerror = () => {
          db.close();
          reject(new Error('Failed to check persisted session'));
        };
      },
    );

    if (!sessionData) {
      return false;
    }

    // Check expiration
    const createdAt = new Date(sessionData.createdAt);
    const durationMinutes = sessionData.sessionDurationMinutes ?? 0;
    const expiresAt = new Date(
      createdAt.getTime() + durationMinutes * 60 * 1000,
    );

    return new Date() <= expiresAt;
  } catch (error) {
    logger.error('Failed to check persisted session', error as Error);
    return false;
  }
}

/**
 * Clear all persisted sessions (all users)
 *
 * Used for development cleanup or security-sensitive operations.
 */
export async function clearAllPersistedSessions(): Promise<void> {

  try {
    const db = await initDB();

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        db.close();
        resolve();
      };

      request.onerror = () => {
        db.close();
        logger.error('Failed to clear all persisted sessions');
        reject(new Error('Failed to clear all persisted sessions'));
      };
    });
  } catch (error) {
    logger.error('Failed to clear all persisted sessions', error as Error);
  }
}
